import React, { useMemo, useState } from 'react';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';

// Compact Brand → Collection → Material → Color picker used to (re)link a visualization to a real catalog color.
export const VizColorLinker = ({ allColors, value, onChange }) => {
  const [q, setQ] = useState('');
  const [brand, setBrand] = useState(value?.brand_id || '');
  const [coll, setColl] = useState(value?.collection_id || '');
  const [mat, setMat] = useState(value?.material_id || '');

  const brands = useMemo(() => [...new Map(allColors.map(c => [c.brand_id, c.brand_name])).entries()], [allColors]);
  const colls = useMemo(() => [...new Map(allColors.filter(c => !brand || c.brand_id === brand).filter(c => c.collection_id).map(c => [c.collection_id, c.collection_name])).entries()], [allColors, brand]);
  const mats = useMemo(() => [...new Map(allColors.filter(c => c.collection_id === coll && c.material_id).map(c => [c.material_id, c.material_name])).entries()], [allColors, coll]);
  const colors = useMemo(() => {
    const s = q.trim().toLowerCase();
    return allColors.filter(c =>
      (!brand || c.brand_id === brand) && (!coll || c.collection_id === coll) && (!mat || c.material_id === mat) &&
      (!s || (c.name || '').toLowerCase().includes(s) || (c.code || '').toLowerCase().includes(s))
    ).slice(0, 60);
  }, [allColors, brand, coll, mat, q]);

  const sel = (cls) => `w-full border rounded-md p-2 text-xs ${cls || ''}`;

  return (
    <div className="space-y-2 p-3 bg-neutral-50 rounded-lg border" data-testid="viz-color-linker">
      <Label className="text-xs font-semibold">Link to catalog color</Label>
      <div className="grid grid-cols-3 gap-2">
        <select value={brand} onChange={e => { setBrand(e.target.value); setColl(''); setMat(''); }} className={sel()} data-testid="viz-link-brand">
          <option value="">Brand…</option>
          {brands.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select value={coll} onChange={e => { setColl(e.target.value); setMat(''); }} className={sel()} data-testid="viz-link-collection">
          <option value="">Collection…</option>
          {colls.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <select value={mat} onChange={e => setMat(e.target.value)} className={sel()} disabled={!mats.length} data-testid="viz-link-material">
          <option value="">{mats.length ? 'Material…' : '— no materials —'}</option>
          {mats.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search color name or article code…" className="h-8 text-xs" data-testid="viz-link-search" />
      <div className="max-h-40 overflow-y-auto grid grid-cols-2 gap-1">
        {colors.map(c => (
          <button
            key={`${c.collection_id}:${c.material_id}:${c.id}`} type="button" onClick={() => onChange(c)}
            className={`flex items-center gap-2 p-1.5 rounded border text-left text-xs ${value?.color_id === c.id ? 'border-amber-500 bg-amber-50' : 'border-neutral-200 bg-white hover:border-amber-300'}`}
            data-testid={`viz-link-color-${c.id}`}
          >
            <span className="w-5 h-5 rounded border flex-shrink-0" style={{ backgroundColor: c.hex_color || '#ddd' }} />
            <span className="min-w-0">
              <span className="block truncate font-medium">{c.name}</span>
              <span className="block truncate text-[10px] text-neutral-500">{c.code || '—'} · {c.material_name || c.collection_name}</span>
            </span>
          </button>
        ))}
        {colors.length === 0 && <p className="col-span-2 text-xs text-neutral-400 text-center py-3">No colors match</p>}
      </div>
      {value?.color_id && (
        <p className="text-[11px] text-emerald-700" data-testid="viz-link-selected">
          Linked: {value.brand_name} › {value.collection_name}{value.material_name ? ` › ${value.material_name}` : ''} › {value.color_name} {value.color_code ? `(${value.color_code})` : ''}
        </p>
      )}
    </div>
  );
};

export default VizColorLinker;
